import { Controller, FieldError } from 'react-hook-form';
import Select, { components, InputProps } from 'react-select';
import FormField from '../FormField/FormField';
import { errorId } from '../FormField/errorId';

type FormField = {
  id: string;
  type: string;
  label: string;
  required: boolean;
  options?: Option[];
  placeholder?: string;
  position: number;
};

type Option = { value: string; label: string };

type Props = {
  field: FormField;
  error?: FieldError;
  control: any;
};

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true" style={{ color: 'var(--pe-check)', flex: 'none' }}>
    <path d="M4.5 10.5l3.5 3.5 7.5-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// react-select points aria-describedby at its placeholder; add the error message too
const DescribedInput = (props: InputProps<Option, false>) => {
  const describedBy = [props['aria-describedby'], props.selectProps['aria-errormessage']]
    .filter(Boolean)
    .join(' ');
  return <components.Input {...props} aria-describedby={describedBy || undefined} />;
};

const FormDropdown = ({ field, error, control }: Props) => {
  const { id, label, required, options, position } = field;

  const uniqueId = id == "none" ? `${id}-${position}` : id;

  return (
    <FormField
      htmlFor={uniqueId}
      label={label}
      required={required}
      error={error ? error.message || 'Choose an option' : undefined}
    >
      <Controller
        control={control}
        name={uniqueId}
        rules={{ required }}
        render={({ field: { onChange, onBlur, value, ref } }: any) => (
          <Select
            ref={ref}
            data-testid={uniqueId}
            inputId={uniqueId}
            aria-invalid={!!error}
            required={required}
            aria-errormessage={error ? errorId(uniqueId) : undefined}
            components={{ Input: DescribedInput }}
            onBlur={onBlur}
            onChange={(selectedOption) => {
              onChange(selectedOption?.label);
            }}
            value={options?.find(option => option.label === value)}
            options={options}
            placeholder={field.placeholder || "Select an option..."}
            formatOptionLabel={(option, { context }) =>
              context === 'menu' && option.label === value ? (
                <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span>{option.label}</span>
                  <CheckIcon />
                </span>
              ) : option.label
            }
            styles={{
              control: (base, state) => ({
                ...base,
                minHeight: '48px',
                padding: '0 6px 0 4px',
                borderRadius: '12px',
                borderColor: error
                  ? 'var(--pe-err-border)'
                  : state.isFocused ? 'var(--pe-focus)' : 'var(--pe-border)',
                boxShadow: state.isFocused
                  ? `inset 0 0 0 1px ${error ? 'var(--pe-err-border)' : 'var(--pe-focus)'}`
                  : 'none',
                background: error && !state.isFocused ? 'var(--pe-err-tint)' : 'var(--pe-input-bg)',
                cursor: 'pointer',
                transition: 'border-color 150ms ease, box-shadow 150ms ease',
                '&:hover': {
                  borderColor: error
                    ? 'var(--pe-err-border)'
                    : state.isFocused ? 'var(--pe-focus)' : 'var(--pe-border-hover)',
                },
              }),
              valueContainer: (base) => ({ ...base, padding: '0 8px' }),
              singleValue: (base) => ({ ...base, color: 'var(--pe-input-text)', margin: 0 }),
              placeholder: (base) => ({ ...base, color: 'var(--pe-placeholder)', margin: 0 }),
              input: (base) => ({ ...base, color: 'var(--pe-input-text)', margin: 0 }),
              indicatorSeparator: () => ({ display: 'none' }),
              dropdownIndicator: (base, state) => ({
                ...base,
                color: 'var(--pe-muted)',
                transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 150ms ease',
                '&:hover': { color: 'var(--pe-muted)' },
              }),
              menu: (base) => ({
                ...base,
                marginTop: '6px',
                background: 'var(--pe-input-bg)',
                border: '1px solid var(--pe-border)',
                borderRadius: '12px',
                boxShadow: 'var(--pe-menu-shadow)',
                zIndex: 5,
              }),
              menuList: (base) => ({
                ...base,
                padding: '6px',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }),
              option: (base, state) => ({
                ...base,
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                padding: '0 12px',
                borderRadius: '8px',
                color: 'var(--pe-input-text)',
                fontSize: '15px',
                fontWeight: state.isSelected ? 600 : 400,
                background: state.isFocused ? 'var(--pe-highlight)' : 'transparent',
                cursor: 'pointer',
                ':active': { background: 'var(--pe-highlight)' },
              }),
            }}
          />
        )}
      />
    </FormField>
  )
};

export default FormDropdown;
